import { App, Route, NavItem } from "@/lib/types";
import { APPS } from "@/lib/constants";
import { EntityInstanceDto, EntityPermission } from "../types/auth";

export interface PermissionChecker {
  hasEntityAction: (entityType: string, action: string) => boolean;
  isInGroup: (group: string) => boolean;
}

export const checkRoutePermission = (
  route: Route,
  checker: PermissionChecker
): boolean => {
  if (!route.permission) return true;

  const { entityType, action, group } = route.permission;

  // Check group requirement first
  if (group && !checker.isInGroup(group)) return false;

  // Normalize actions into an array
  const actions = Array.isArray(action) ? action : [action];

  // Tab is allowed if user has ANY of the actions
  return actions.some((act) => checker.hasEntityAction(entityType, act));
};

export const getAuthorizedApps = (checker: PermissionChecker): App[] => {
  return Object.values(APPS).filter((app) =>
    app.routes.some((route) => checkRoutePermission(route, checker))
  );
};

export const getAuthorizedRoutes = (
  app: App,
  checker: PermissionChecker
): Route[] => {
  return app.routes.filter((route) => checkRoutePermission(route, checker));
};

export const getNavItems = (
  app: App,
  checker: PermissionChecker
): NavItem[] => {
  return app.routes
    .filter((route) => route.nav && checkRoutePermission(route, checker))
    .map((route) => ({
      title: route.nav!.title,
      url: route.path,
      icon: route.nav!.icon,
    }));
};

export const getAppByPath = (
  pathname: string,
  authorizedApps: App[]
): App | null => {
  return (
    authorizedApps.find((app) => pathname.startsWith(app.basePath)) || null
  );
};

export const getUniqueEntitiesFromPermissions = (
  permissions: Array<EntityPermission>
): EntityInstanceDto[] => {
  const entityMap = new Map<string, EntityInstanceDto>();

  permissions.forEach((permission) => {
    const { entity } = permission;
    const key = `${entity.entity_id}-${entity.entity_type}`;

    if (!entityMap.has(key)) {
      entityMap.set(key, {
        entity_id: entity.entity_id,
        entity_type: entity.entity_type,
        entity_name: entity.entity_name,
      });
    }
  });

  return Array.from(entityMap.values());
};
